import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Search, ShieldAlert, BookOpen } from "lucide-react";
import { SdsDetail } from "../components/SdsDetail";
import { SdsStation, downloadBinder } from "../components/SdsStation";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { api, fmtErr, fmtDate, fileUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CHEMICAL_FIELDS } from "../lib/registers";
import ModulePage from "../components/ModulePage";
import { FormDialog } from "../components/FormDialog";
import { RagBadge } from "../components/Badges";
import { Button } from "../components/ui/button";

const Flags = ({ flags }) => (
  <div className="flex flex-wrap gap-1">
    {flags.map((f, i) => <RagBadge key={i} status={f.level} label={f.label} />)}
    {!flags.length && <RagBadge status="green" label="All checks passed" />}
  </div>
);

const Inventory = () => {
  const { isManager } = useAuth();
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState("");
  const [info, setInfo] = useState(null);
  const load = useCallback(() => api.get("/chemicals").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const save = async (v) => {
    try {
      if (v.id) await api.put(`/chemicals/${v.id}`, v);
      else await api.post("/chemicals", v);
      toast.success("Chemical saved");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
      throw e;
    }
  };
  const shown = rows.filter((r) => !q || JSON.stringify(r).toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-4" data-testid="chemical-inventory">
      <div className="flex flex-wrap gap-3">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
          <input className="field pl-9" placeholder="Search product, CAS, barcode..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="chemical-search" />
        </div>
        <Button variant="outline" onClick={downloadBinder} className="tap border-slate-700 bg-transparent" data-testid="inventory-binder-btn"><BookOpen className="mr-1 h-4 w-4" />SDS binder</Button>
        {isManager && <Button onClick={() => setEdit({ approval_status: "pending" })} className="tap bg-rose-600 hover:bg-rose-700" data-testid="add-chemical-btn"><Plus className="mr-1 h-4 w-4" />Add chemical</Button>}
      </div>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {shown.map((c) => (
          <div key={c.id} className="panel min-w-0 p-4" data-testid={`chemical-card-${c.cas_number || c.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 break-words">
                <p className="font-semibold">{c.product_name} {c.restricted && <span className="ml-1 rounded bg-red-600 px-1.5 text-[10px] uppercase text-white">Restricted</span>}</p>
                <p className="text-xs text-slate-400">{c.chemical_name} - CAS <span className="font-mono">{c.cas_number || "-"}</span> - {c.un_number || "no UN"}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <RagBadge status={c.rag} />
                {isManager && <Button size="icon" variant="ghost" className="h-9 w-9" onClick={() => setEdit(c)} data-testid={`edit-chemical-${c.id}`}><Pencil className="h-4 w-4" /></Button>}
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-300 sm:grid-cols-4">
              <p><span className="eyebrow block text-[9px]">Storage</span>{c.storage_area || "-"}</p>
              <p><span className="eyebrow block text-[9px]">Qty / Max</span><span className="font-mono">{c.current_quantity ?? "-"}/{c.max_quantity ?? "-"} {c.unit}</span></p>
              <p><span className="eyebrow block text-[9px]">SDS expiry</span>{fmtDate(c.sds_expiry)}</p>
              <p><span className="eyebrow block text-[9px]">Hazards</span>{(c.hazard_classes || []).join(", ") || "-"}</p>
            </div>
            <div className="mt-3"><Flags flags={c.flags} /></div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="border-red-700 bg-transparent text-red-200" onClick={() => setInfo(c)} data-testid={`sds-info-${c.id}`}><ShieldAlert className="mr-1 h-4 w-4" />First aid &amp; spill</Button>
              {c.sds_path && !c.sds_path.startsWith("seed/") && <a href={fileUrl(c.sds_path)} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="border-slate-700 bg-transparent" data-testid={`view-sds-${c.id}`}>View SDS</Button></a>}
              {c.coshh_assessment && <a href={fileUrl(c.coshh_assessment)} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="border-slate-700 bg-transparent" data-testid={`view-coshh-${c.id}`}>COSHH assessment</Button></a>}
              {c.sds?.sample && <span className="self-center text-[10px] uppercase text-slate-500">Sample SDS</span>}
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!info} onOpenChange={(o) => !o && setInfo(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-slate-700 bg-ink-2 sm:max-w-2xl" data-testid="sds-info-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl uppercase">{info?.product_name}</DialogTitle></DialogHeader>
          {info && <SdsDetail c={info} />}
        </DialogContent>
      </Dialog>
      <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title={edit?.id ? "Edit chemical" : "New chemical"} fields={CHEMICAL_FIELDS} initial={edit} onSubmit={save} testid="chemical-form" />
    </div>
  );
};

const CHECKS = [["approved_chemical", "Approved chemical?"], ["coshh_completed", "COSHH assessment completed?"], ["sds_received", "SDS received?"], ["storage_available", "Storage available?"], ["restricted_check_passed", "Restricted list check passed?"]];

const Purchases = () => {
  const { canWrite, isApprover } = useAuth();
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ checks: {} });
  const load = useCallback(() => api.get("/chemical-requests").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    try {
      await api.post("/chemical-requests", f);
      toast.success("Purchase request submitted for approval");
      setF({ checks: {} });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const decide = async (r, decision) => {
    try {
      await api.post(`/chemical-requests/${r.id}/decision`, { decision, checks: r.checks });
      toast.success(`Request ${decision}d`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const toggle = (r, k) => setRows(rows.map((x) => (x.id === r.id ? { ...x, checks: { ...x.checks, [k]: !x.checks[k] } } : x)));
  return (
    <div className="grid gap-4 lg:grid-cols-[380px_1fr]">
      {canWrite && (
        <div className="panel h-fit space-y-3 p-4" data-testid="purchase-request-form">
          <h3 className="font-display text-lg font-bold uppercase">New purchase request</h3>
          <input className="field" placeholder="Product name *" value={f.product_name || ""} onChange={(e) => setF({ ...f, product_name: e.target.value })} data-testid="pr-product" />
          <input className="field" placeholder="Supplier" value={f.supplier || ""} onChange={(e) => setF({ ...f, supplier: e.target.value })} data-testid="pr-supplier" />
          <input className="field" placeholder="Quantity" value={f.quantity || ""} onChange={(e) => setF({ ...f, quantity: e.target.value })} data-testid="pr-quantity" />
          {CHECKS.map(([k, l]) => (
            <label key={k} className="flex items-center gap-3 text-sm"><input type="checkbox" className="h-5 w-5 accent-rose-600" checked={!!f.checks[k]} onChange={(e) => setF({ ...f, checks: { ...f.checks, [k]: e.target.checked } })} data-testid={`pr-check-${k}`} />{l}</label>
          ))}
          <Button onClick={submit} disabled={!f.product_name} className="tap w-full bg-rose-600 hover:bg-rose-700" data-testid="pr-submit-btn">Submit request</Button>
        </div>
      )}
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.id} className="panel p-4" data-testid={`pr-card-${r.id}`}>
            <div className="flex items-center justify-between"><p className="font-semibold">{r.product_name}</p><RagBadge status={{ approved: "green", rejected: "red", pending: "amber" }[r.status]} label={r.status} /></div>
            <p className="text-xs text-slate-400">{r.supplier} - {r.quantity} - requested by {r.requested_by}</p>
            <div className="mt-2 grid gap-1 sm:grid-cols-2">
              {CHECKS.map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 text-xs">
                  <input type="checkbox" disabled={!isApprover || r.status !== "pending"} className="h-4 w-4 accent-emerald-600" checked={!!r.checks[k]} onChange={() => toggle(r, k)} data-testid={`pr-${r.id}-check-${k}`} />
                  <span className={r.checks[k] ? "text-emerald-300" : "text-red-300"}>{l}</span>
                </label>
              ))}
            </div>
            {isApprover && r.status === "pending" && (
              <div className="mt-3 flex gap-2">
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => decide(r, "approve")} data-testid={`pr-approve-${r.id}`}>Approve</Button>
                <Button size="sm" variant="outline" className="border-slate-700 bg-transparent" onClick={() => decide(r, "reject")} data-testid={`pr-reject-${r.id}`}>Reject</Button>
              </div>
            )}
          </div>
        ))}
        {!rows.length && <p className="panel p-8 text-center text-sm text-slate-500">No purchase requests.</p>}
      </div>
    </div>
  );
};

export default function Coshh() {
  return (
    <ModulePage eyebrow="Module 04" title="Chemical Inventory & COSHH" module="coshh"
      description="Central chemical database with automatic checks for missing or expired SDS, incompatible storage, restricted substances and quantity exceedances."
      templates={["coshh_point_of_use"]}
      tabs={[{ key: "inventory", label: "Inventory", element: <Inventory /> }, { key: "sds-station", label: "SDS Station & QR", element: <SdsStation /> }, { key: "purchases", label: "Purchase approval", element: <Purchases /> }]} />
  );
}
